import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Type, Palette, BarChart2, Clock, Split, Brain, CreditCard, XCircle,
  Target, HelpCircle, Zap, Users, CheckCircle2, BarChart3, Smartphone, TrendingUp,
  Layout, ShoppingBag, LayoutTemplate, RefreshCw, Check, MousePointer2, ArrowUpRight,
  Flame, ArrowDown, Sparkles, Settings, Layers, Filter, Video
} from 'lucide-react';
import Header from '../../Components/Header';
import Footer from '../../Components/Footer';
import SimpleKakaoButton from '../../Components/SimpleKakaoButton';

const ServiceAbtest = () => {
  const [scrolled, setScrolled] = useState(false);
  const [trafficRatio, setTrafficRatio] = useState(50);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  // 활용 사례
  const cases = [
    {
      title: "CTA 버튼 최적화",
      category: "E-Commerce",
      problem: "메인 배너의 '구매하기' 버튼 클릭률이 1.2%로 저조함",
      solution: "버튼 색상을 브랜드 컬러(Blue)에서 보색(Orange)으로 변경하고, 문구를 '지금 시작'으로 수정",
      metric: "+45%",
      metricLabel: "클릭률(CTR) 상승",
      icon: <ShoppingBag className="w-5 h-5" />,
      color: "orange"
    },
    {
      title: "랜딩 페이지 리뉴얼",
      category: "SaaS / B2B",
      problem: "복잡한 기능 설명으로 인해 이탈률이 75%에 육박",
      solution: "스크롤 길이를 줄이고, 핵심 가치(Value Prop) 위주의 간결한 레이아웃으로 변경 (Split URL)",
      metric: "+32%",
      metricLabel: "가입 전환율 상승",
      icon: <LayoutTemplate className="w-5 h-5" />,
      color: "blue"
    },
    {
      title: "가격 정책 테스트",
      category: "Subscription",
      problem: "연간 결제 비율이 낮아 LTV(고객 생애 가치)가 낮음",
      solution: "월간 vs 연간 요금을 나란히 배치하고, 연간 결제 시 '2개월 무료' 뱃지 강조",
      metric: "2.1x",
      metricLabel: "연간 결제 비중 증가",
      icon: <CreditCard className="w-5 h-5" />,
      color: "green"
    },
    {
      title: "모바일 UI 개선",
      category: "Mobile App",
      problem: "모바일 웹에서의 장바구니 포기율이 데스크탑보다 2배 높음",
      solution: "하단 고정(Sticky) 결제 버튼을 추가하여 접근성 개선",
      metric: "-18%",
      metricLabel: "이탈률 감소",
      icon: <Smartphone className="w-5 h-5" />,
      color: "purple"
    }
  ];

  // 주요 특징
  const features = [
    {
      icon: <Zap className="w-6 h-6 text-blue-600" />,
      title: '코드 없는 간편 설정',
      description: '개발자 없이도 마케터가 직접 테스트를 설정하고 관리할 수 있습니다.'
    },
    {
      icon: <BarChart2 className="w-6 h-6 text-blue-600" />,
      title: '실시간 결과 분석',
      description: '테스트 결과를 실시간으로 확인하고 즉각적인 의사결정이 가능합니다.'
    },
    {
      icon: <Users className="w-6 h-6 text-blue-600" />,
      title: '유연한 트래픽 분배',
      description: '테스트 상황에 맞춰 트래픽 비율을 자유롭게 설정할 수 있습니다.'
    },
    {
      icon: <Target className="w-6 h-6 text-blue-600" />,
      title: '통계적 신뢰도',
      description: '과학적 분석으로 명확한 승자를 판정하고 확신 있는 결정을 내릴 수 있습니다.'
    }
  ];

  return (
    <div className="bg-white">
      {/* 플로팅 CTA 버튼 */}
      <div className={`fixed bottom-6 left-6 z-50 transition-all duration-300 ${scrolled ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <Link to="/freestart">
          <button className="flex items-center bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group">
            <span className="font-medium">무료로 시작하기</span>
            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </button>
        </Link>
      </div>

      <Header />

      {/* 1. Hero Section: 3D Elements & Gradient */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-[#0B0F19]">
        {/* 1. 배경: 격자무늬 & 딥 블루 그라데이션 */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:40px_40px]"></div>

        {/* 배경 조명 (Cool Tone) */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-indigo-600/10 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* Left: 텍스트 (A/B Test) */}
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm font-semibold mb-8 animate-fade-in-up">
                <Split className="w-4 h-4" />
                <span>Powerful A/B Testing Platform</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
                데이터로 증명하는 <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400">성장의 방정식</span>
              </h1>

              <p className="text-base sm:text-xl text-gray-400 mb-10 leading-relaxed max-w-lg">
                더 이상 '감'에 의존하지 마세요.
                두 가지 버전을 비교 실험하고, 가장 과학적인 방법으로 고객이 원하는 <br className="sm:hidden" />최적의 경험을 찾아냅니다.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/freestart">
                  <button className="px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-lg shadow-lg shadow-blue-900/20 hover:shadow-blue-500/30 transition-all hover:-translate-y-1">
                    무료로 테스트 시작하기
                  </button>
                </Link>
                <Link to="/service/heatmap">
                  <button className="px-8 py-4 bg-white/5 border border-white/10 hover:bg-white/10 text-white rounded-xl font-medium text-lg transition-all backdrop-blur-sm flex items-center justify-center gap-2 group">
                    히트맵 기능 보기
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </Link>
              </div>

              {/* 하단 신뢰 문구 */}
              <div className="mt-8 flex items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-1 text-green-400">
                  <BarChart2 className="w-4 h-4" />
                  <span className="font-bold">Avg +24%</span>
                </div>
                <p>평균 전환율 상승 효과</p>
              </div>
            </div>

            {/* Right: 3D A/B 테스트 비주얼 (Upgrade) */}
            <div className="relative perspective-1000 hidden lg:block">
              {/* 3D Tilt Container */}
              <div className="relative transform rotate-y-[-12deg] rotate-x-[5deg] transition-transform duration-700 hover:rotate-y-[-5deg] hover:rotate-x-[2deg]">

                {/* Browser Window */}
                <div className="bg-[#1A1F2E] border border-gray-700/50 rounded-2xl shadow-2xl overflow-hidden min-h-[500px] relative">

                  {/* Browser Header */}
                  <div className="bg-[#0F131C] px-4 py-3 flex items-center gap-2 border-b border-gray-800">
                    <div className="flex gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-slate-600"></div>
                    </div>
                    <div className="ml-4 bg-[#1A1F2E] px-3 py-1 rounded text-[10px] text-gray-500 font-mono flex-1 text-center">
                      switchup.io/experiment/dashboard
                    </div>
                  </div>

                  {/* Dashboard Content */}
                  <div className="p-8">
                    {/* Title */}
                    <div className="flex justify-between items-center mb-8">
                      <div>
                        <div className="text-gray-400 text-xs mb-1">테스트 ID #8291</div>
                        <div className="text-white font-bold text-lg">메인 랜딩 CTA 테스트</div>
                      </div>
                      <div className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-xs font-bold border border-green-500/30 animate-pulse">
                        실시간 진행 중
                      </div>
                    </div>

                    {/* A/B Comparison Cards */}
                    <div className="grid grid-cols-2 gap-6">

                      {/* Variant A (Original) */}
                      <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700 relative">
                        <div className="text-xs text-gray-500 font-bold mb-4 uppercase tracking-wider">기존안 (A)</div>
                        <div className="flex items-end gap-2 mb-2">
                          <div className="text-3xl font-bold text-gray-300">2.4%</div>
                          <div className="text-xs text-gray-500 mb-1">전환율</div>
                        </div>
                        {/* Graph */}
                        <div className="h-16 flex items-end gap-1">
                          {[20, 35, 30, 45, 35, 40].map((h, i) => (
                            <div key={i} className="flex-1 bg-slate-600 rounded-t-sm" style={{ height: `${h}%` }}></div>
                          ))}
                        </div>
                      </div>

                      {/* Variant B (Winner) */}
                      <div className="bg-blue-900/20 rounded-xl p-5 border border-blue-500/50 relative overflow-hidden">
                        {/* Winner Badge */}
                        <div className="absolute top-0 right-0 bg-blue-600 text-white text-[10px] font-bold px-2 py-1 rounded-bl-lg shadow-lg z-10">
                          승리 🏆
                        </div>

                        <div className="text-xs text-blue-300 font-bold mb-4 uppercase tracking-wider">변경안 (B)</div>
                        <div className="flex items-end gap-2 mb-2">
                          <div className="text-3xl font-bold text-white">4.8%</div>
                          <div className="text-xs text-green-400 mb-1 bg-green-900/30 px-1 rounded font-bold">▲ 100%</div>
                        </div>

                        {/* Glowing Graph */}
                        <div className="h-16 flex items-end gap-1 relative">
                          <div className="absolute inset-0 bg-blue-500/20 blur-xl"></div>
                          {[30, 55, 60, 85, 75, 90].map((h, i) => (
                            <div key={i} className="flex-1 bg-blue-500 rounded-t-sm relative z-10" style={{ height: `${h}%` }}></div>
                          ))}
                        </div>
                      </div>

                    </div>

                    {/* Confidence Bar */}
                    <div className="mt-8">
                      <div className="flex justify-between text-xs mb-2">
                        <span className="text-gray-400">통계적 신뢰도</span>
                        <span className="text-blue-400 font-bold">99.1%</span>
                      </div>
                      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                        <div className="h-full w-[99%] bg-gradient-to-r from-blue-600 to-indigo-500 rounded-full shadow-[0_0_10px_rgba(59,130,246,0.5)]"></div>
                      </div>
                    </div>

                  </div>
                </div>

                {/* Reflection */}
                <div className="absolute -bottom-10 left-10 right-10 h-20 bg-blue-500/20 blur-[60px] rounded-full transform scale-y-50"></div>
              </div>
            </div>

          </div>
        </div>

        {/* Shared Style for 3D */}
        <style>{`
        .perspective-1000 { perspective: 1000px; }
      `}</style>
      </section>

      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* 헤더 */}
          <div className="text-center mb-16">
            <div className="inline-block px-3 py-1 bg-gray-200 text-gray-600 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              Why A/B Testing?
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              '감'은 틀릴 수 있지만, <br className="sm:hidden" />
              <span className="text-blue-600">'데이터'</span>는 거짓말하지 <br className="sm:hidden" />않습니다.
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              성공적인 기업은 회의실의 목소리보다 <br className="sm:hidden" />고객의 행동 데이터를 더 신뢰합니다.
            </p>
          </div>

          {/* 비교 카드 영역 */}
          <div className="relative max-w-5xl mx-auto">
            {/* 중앙 VS 배지 (데스크탑용) */}
            <div className="hidden md:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-white rounded-full items-center justify-center shadow-lg z-10 font-black text-gray-300 italic text-xl border-4 border-gray-50">
              VS
            </div>

            <div className="grid md:grid-cols-2 gap-8 md:gap-0">

              {/* 1. 기존 방식 (Old Way) - 흐릿하고 정적인 느낌 */}
              <div className="bg-white p-8 md:p-12 rounded-2xl md:rounded-r-none md:rounded-l-2xl border border-gray-200 shadow-sm relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gray-100 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-110"></div>

                <div className="relative z-10">
                  <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mb-6 text-gray-400">
                    <Brain className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-500 mb-2">직관에 의한 결정</h3>
                  <p className="text-gray-400 text-sm mb-8 font-medium">Guesswork & Opinions</p>

                  <ul className="space-y-6">
                    {[
                      { text: "이 디자인이 더 예쁜 것 같아", sub: "주관적 의견" },
                      { text: "경쟁사가 이렇게 하니까 따라하자", sub: "맥락 없는 모방" },
                      { text: "실패 시 정확한 원인을 모름", sub: "학습 효과 없음" }
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-4 opacity-70 group-hover:opacity-100 transition-opacity">
                        <div className="mt-1 min-w-[24px]">
                          <HelpCircle className="w-6 h-6 text-gray-300" />
                        </div>

                        {/* 텍스트 영역 컨테이너 */}
                        <div className="flex-1">
                          <div className="text-gray-600 font-medium text-base sm:text-lg break-keep">
                            "{item.text}"
                          </div>
                          <div className="text-gray-400 text-xs mt-0.5">{item.sub}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* 2. 새로운 방식 (New Way) - 선명하고 강조된 느낌 */}
              <div className="bg-white p-8 md:p-12 rounded-2xl md:rounded-l-none md:rounded-r-2xl border border-blue-100 shadow-xl relative overflow-hidden transform md:scale-105 md:-my-4 z-0 ring-1 ring-blue-500/10">
                {/* 배경 장식 */}
                <div className="absolute inset-0 bg-blue-50/50"></div>
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full blur-3xl opacity-60 -mr-20 -mt-20"></div>

                <div className="relative z-10">
                  <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center mb-6 text-white shadow-lg shadow-blue-500/30">
                    <BarChart3 className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">데이터 기반 결정</h3>
                  <p className="text-blue-600 text-sm mb-8 font-bold tracking-wide">Data-Driven & Certainty</p>

                  <ul className="space-y-6">
                    {[
                      { text: "A버전의 전환율이 15% 더 높음", sub: "수치로 증명됨" },
                      { text: "고객 행동 데이터에 기반한 설계", sub: "과학적 근거" },
                      { text: "실패해도 인사이트를 얻음", sub: "지속적인 성장" }
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-4">
                        <div className="mt-1 min-w-[24px]">
                          <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                            <Check className="w-4 h-4 text-blue-600 stroke-[3]" />
                          </div>
                        </div>
                        <div>
                          <div className="text-gray-900 font-bold text-lg">{item.text}</div>
                          <div className="text-blue-600/70 text-xs mt-0.5 font-medium">{item.sub}</div>
                        </div>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-10 pt-8 border-t border-blue-100">
                    <div className="flex items-center gap-2 text-blue-800 font-bold">
                      <span>성공 확률 3.5배 증가</span>
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* 헤더 */}
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              두 가지 강력한 테스트 엔진
            </h2>
            <p className="text-xl text-gray-600">
              상황에 맞는 최적의 방식을 선택하세요
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">

            {/* 1. URL 분할 테스트 (Split URL) */}
            <div className="group relative rounded-2xl border border-gray-200 bg-white p-8 hover:border-blue-500 hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-300 overflow-hidden">
              {/* 상단: 아이콘 & 타이틀 */}
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-colors duration-300">
                  <Split className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors" />
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                  URL 분할 테스트
                </h3>
                <p className="text-sm font-semibold text-blue-500 mb-4 uppercase tracking-wide">
                  Split URL Testing
                </p>

                <p className="text-sm sm:text-base text-gray-600 mb-8 leading-relaxed h-12">
                  서로 다른 URL(예: /home-a, /home-b)로 트래픽을 나누어 테스트합니다.
                  <br className="hidden sm:block" /> 페이지 전체 구조가 다르거나 대대적인 리뉴얼 시 적합합니다.
                </p>

                {/* ⭐ Mini Visualization: Traffic Split (Flow Animation) ⭐ */}
                <div className="h-48 bg-slate-50 rounded-xl border border-slate-100 mb-8 flex items-center justify-center relative overflow-hidden group-hover:bg-blue-50/30 transition-colors">

                  {/* Background Grid */}
                  <div className="absolute inset-0 bg-[linear-gradient(to_right,#e2e8f0_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f0_1px,transparent_1px)] bg-[size:16px_16px] opacity-40"></div>

                  {/* SVG Animation Container */}
                  <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 400 200">
                    <defs>
                      {/* Path A (Upper Curve) */}
                      <path id="pathA" d="M 40,100 C 140,100 160,60 320,60" fill="none" />
                      {/* Path B (Lower Curve) */}
                      <path id="pathB" d="M 40,100 C 140,100 160,140 320,140" fill="none" />
                    </defs>

                    {/* Connecting Lines (Dashed) */}
                    <path d="M 40,100 C 140,100 160,60 320,60" stroke="#CBD5E1" strokeWidth="2" strokeDasharray="6 4" fill="none" />
                    <path d="M 40,100 C 140,100 160,140 320,140" stroke="#CBD5E1" strokeWidth="2" strokeDasharray="6 4" fill="none" />

                    {/* Moving Particles (Traffic) */}
                    {/* Particle to A */}
                    <circle r="4" fill="#3B82F6">
                      <animateMotion dur="2s" repeatCount="indefinite" keyPoints="0;1" keyTimes="0;1">
                        <mpath href="#pathA" />
                      </animateMotion>
                      <animate attributeName="opacity" values="0;1;1;0" dur="2s" repeatCount="indefinite" />
                    </circle>
                    <circle r="4" fill="#3B82F6">
                      <animateMotion dur="2s" begin="1s" repeatCount="indefinite" keyPoints="0;1" keyTimes="0;1">
                        <mpath href="#pathA" />
                      </animateMotion>
                      <animate attributeName="opacity" values="0;1;1;0" dur="2s" begin="1s" repeatCount="indefinite" />
                    </circle>

                    {/* Particle to B */}
                    <circle r="4" fill="#6366F1">
                      <animateMotion dur="2s" begin="0.5s" repeatCount="indefinite" keyPoints="0;1" keyTimes="0;1">
                        <mpath href="#pathB" />
                      </animateMotion>
                      <animate attributeName="opacity" values="0;1;1;0" dur="2s" begin="0.5s" repeatCount="indefinite" />
                    </circle>
                    <circle r="4" fill="#6366F1">
                      <animateMotion dur="2s" begin="1.5s" repeatCount="indefinite" keyPoints="0;1" keyTimes="0;1">
                        <mpath href="#pathB" />
                      </animateMotion>
                      <animate attributeName="opacity" values="0;1;1;0" dur="2s" begin="1.5s" repeatCount="indefinite" />
                    </circle>
                  </svg>

                  {/* --- Elements Layer --- */}

                  {/* Source Icon (Users) */}
                  <div className="absolute left-6 top-1/2 -translate-y-1/2 w-10 h-10 bg-white border border-slate-200 rounded-full flex items-center justify-center shadow-sm z-10">
                    <Users className="w-5 h-5 text-slate-400" />
                  </div>

                  {/* Splitter Icon (Center) */}
                  <div className="absolute left-[130px] top-1/2 -translate-y-1/2 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center z-20 ring-4 ring-white shadow-sm">
                    <Split className="w-4 h-4 text-blue-600" />
                  </div>

                  {/* Page A (Top Right) */}
                  <div className="absolute right-8 top-[40px] bg-white border border-slate-200 w-20 h-14 rounded-lg shadow-sm flex flex-col items-center justify-center gap-1 z-10 group-hover:border-blue-300 transition-colors">
                    <div className="w-8 h-1.5 bg-blue-100 rounded-full"></div>
                    <div className="w-12 h-1.5 bg-slate-100 rounded-full"></div>
                    <div className="absolute -right-2 -top-2 w-5 h-5 bg-blue-500 rounded-full text-[9px] text-white flex items-center justify-center font-bold border-2 border-white shadow-sm">A</div>
                  </div>

                  {/* Page B (Bottom Right) */}
                  <div className="absolute right-8 bottom-[40px] bg-white border border-slate-200 w-20 h-14 rounded-lg shadow-sm flex flex-col items-center justify-center gap-1 z-10 group-hover:border-indigo-300 transition-colors">
                    {/* B has different layout */}
                    <div className="flex gap-1">
                      <div className="w-5 h-5 bg-indigo-50 rounded"></div>
                      <div className="flex flex-col gap-1">
                        <div className="w-8 h-1.5 bg-slate-100 rounded-full"></div>
                        <div className="w-6 h-1.5 bg-slate-100 rounded-full"></div>
                      </div>
                    </div>
                    <div className="absolute -right-2 -top-2 w-5 h-5 bg-indigo-500 rounded-full text-[9px] text-white flex items-center justify-center font-bold border-2 border-white shadow-sm">B</div>
                  </div>

                </div>

                {/* 기능 리스트 */}
                <ul className="space-y-3">
                  {['페이지 전체 리뉴얼 검증', '복잡한 구조 변경 테스트', '개발/배포 기반 테스트'].map((item, i) => (
                    <li key={i} className="flex items-center text-sm text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 2. 비주얼 에디터 테스트 (Visual Editor) */}
            <div className="group relative rounded-2xl border border-gray-200 bg-white p-8 hover:border-indigo-500 hover:shadow-2xl hover:shadow-indigo-500/10 transition-all duration-300 overflow-hidden">
              <div className="relative z-10">
                <div className="w-14 h-14 rounded-xl bg-indigo-50 flex items-center justify-center mb-6 group-hover:bg-indigo-600 transition-colors duration-300">
                  <Palette className="w-7 h-7 text-indigo-600 group-hover:text-white transition-colors" />
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-indigo-600 transition-colors">
                  비주얼 에디터 테스트
                </h3>
                <p className="text-sm font-semibold text-indigo-500 mb-4 uppercase tracking-wide">
                  Visual Editor Testing
                </p>

                <p className="text-sm sm:text-base text-gray-600 mb-8 leading-relaxed min-h-[3rem]">
                  코드 수정 없이 웹사이트 위에 에디터를 띄워 <br className="sm:hidden" />텍스트, 이미지 등을 바로 <br className="hidden sm:block" />수정합니다.
                  마케터가 <br className="sm:hidden" />빠르고 간편하게 실험할 때 최적입니다.
                </p>


                {/* ⭐ Mini Visualization: Interactive Editing (Color Change) ⭐ */}
                <div className="h-48 bg-slate-50 rounded-xl border border-slate-100 mb-8 flex items-center justify-center relative overflow-hidden">

                  {/* Background Grid */}
                  <div className="absolute inset-0 bg-[linear-gradient(to_right,#e2e8f0_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f0_1px,transparent_1px)] bg-[size:16px_16px] opacity-40"></div>

                  {/* Mock Browser Window */}
                  <div className="relative bg-white border border-slate-200 w-56 h-36 rounded-lg shadow-md flex flex-col items-center justify-center p-4 z-10">

                    {/* Browser Header */}
                    <div className="absolute top-0 left-0 w-full h-5 bg-slate-50 border-b border-slate-200 rounded-t-lg flex items-center px-2 gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-slate-300"></div>
                      <div className="w-2 h-2 rounded-full bg-slate-300"></div>
                    </div>

                    {/* --- The Button (Target Element) --- */}
                    {/* 애니메이션: Indigo -> Pink로 색상 변경 */}
                    <div className="mt-2 px-6 py-2 rounded-md text-[10px] text-white font-bold shadow-sm animate-button-change">
                      시작하기
                    </div>

                    {/* Wireframe Lines */}
                    <div className="mt-3 w-32 h-1.5 bg-slate-100 rounded-full"></div>
                    <div className="mt-1.5 w-20 h-1.5 bg-slate-100 rounded-full"></div>

                    {/* --- 1. Selection Box (파란색 테두리) --- */}
                    {/* 애니메이션: 나타났다 사라짐 */}
                    <div className="absolute inset-0 border-2 border-blue-500 rounded-lg animate-selection-appear pointer-events-none opacity-0">
                      <div className="absolute -top-3 -left-[2px] bg-blue-500 text-white text-[8px] px-1.5 rounded-t font-bold">
                        Button #CTA
                      </div>
                    </div>

                    {/* --- 2. Floating Property Panel (설정창) --- */}
                    {/* 애니메이션: 우측에서 슬라이드 등장 */}
                    <div className="absolute -right-16 top-4 w-24 bg-white border border-slate-200 rounded-lg shadow-xl p-2 flex flex-col gap-2 animate-panel-slide opacity-0">
                      <div className="flex items-center gap-1 text-[8px] text-slate-500 font-bold border-b border-slate-100 pb-1">
                        <Settings className="w-2.5 h-2.5" /> Properties
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 text-[8px] text-slate-400">
                          <Palette className="w-2.5 h-2.5" /> 색상
                        </div>
                        <div className="flex gap-1">
                          <div className="w-3 h-3 rounded-full bg-indigo-600 border border-slate-200"></div>
                          {/* 타겟 색상 (클릭 효과 애니메이션) */}
                          <div className="w-3 h-3 rounded-full bg-pink-500 border border-slate-200 animate-swatch-click"></div>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 text-[8px] text-slate-400 opacity-50">
                        <Type className="w-2.5 h-2.5" /> 텍스트
                      </div>
                    </div>

                    {/* --- 3. Mouse Cursor Animation --- */}
                    {/* 애니메이션: 이동 경로 (밖 -> 버튼 -> 패널) */}
                    <div className="absolute z-50 animate-cursor-move top-[120%] right-[10%]">
                      <MousePointer2 className="w-5 h-5 text-slate-800 fill-slate-800 stroke-white drop-shadow-md" />
                    </div>

                  </div>

                  {/* CSS Keyframes Definition */}
                  <style>{`
        /* 1. 커서 이동 경로 (총 4초 주기) */
        @keyframes cursor-move {
          0% { top: 120%; right: 10%; opacity: 0; } /* 시작: 화면 밖 */
          15% { top: 50%; right: 50%; opacity: 1; transform: translate(50%, -50%); } /* 버튼 위로 이동 */
          35% { top: 50%; right: 50%; opacity: 1; transform: translate(50%, -50%); } /* 잠시 대기 (선택) */
          50% { top: 35%; right: 5px; opacity: 1; transform: scale(1); } /* 패널 색상표로 이동 */
          60% { top: 35%; right: 5px; opacity: 1; transform: scale(0.8); } /* 클릭 (작아짐) */
          70% { top: 35%; right: 5px; opacity: 1; transform: scale(1); } /* 클릭 해제 */
          90% { opacity: 1; }
          100% { top: 35%; right: 5px; opacity: 0; } /* 페이드 아웃 */
        }

        /* 2. 패널 등장 */
        @keyframes panel-slide {
          0%, 30% { opacity: 0; transform: translateX(10px); }
          40%, 90% { opacity: 1; transform: translateX(0); } /* 커서가 이동할 때 등장 */
          100% { opacity: 0; transform: translateX(0); }
        }

        /* 3. 버튼 색상 변경 */
        @keyframes button-change {
          0%, 55% { background-color: #4f46e5; box-shadow: none; } /* Indigo */
          60%, 100% { background-color: #ec4899; box-shadow: 0 4px 6px -1px rgba(236, 72, 153, 0.3); } /* Pink + Glow */
        }

        /* 4. 선택 박스 등장 */
        @keyframes selection-appear {
          0%, 15% { opacity: 0; }
          20%, 90% { opacity: 1; } /* 커서 도착 시 등장 */
          100% { opacity: 0; }
        }

        /* 5. 색상 스와치 클릭 효과 */
        @keyframes swatch-click {
          0%, 55% { transform: scale(1); ring: 0; }
          60%, 70% { transform: scale(1.2); box-shadow: 0 0 0 2px #60a5fa; } /* 클릭 시 커짐 + 파란 링 */
          100% { transform: scale(1); box-shadow: none; }
        }

        /* 애니메이션 클래스 매핑 */
        .animate-cursor-move { animation: cursor-move 4s ease-in-out infinite; }
        .animate-panel-slide { animation: panel-slide 4s ease-in-out infinite; }
        .animate-button-change { animation: button-change 4s ease-in-out infinite; }
        .animate-selection-appear { animation: selection-appear 4s ease-in-out infinite; }
        .animate-swatch-click { animation: swatch-click 4s ease-in-out infinite; }
      `}</style>
                </div>

                {/* 기능 리스트 */}
                <ul className="space-y-3">
                  {['코딩 없는 즉시 수정', '문구(Copy) 및 버튼 최적화', '실시간 미리보기 제공'].map((item, i) => (
                    <li key={i} className="flex items-center text-sm text-gray-700">
                      <CheckCircle2 className="w-4 h-4 text-indigo-500 mr-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

          </div>
        </div>
      </section>

      <section className="py-24 bg-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-16">

            {/* 왼쪽: 설명 텍스트 */}
            <div className="flex-1 max-w-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
                <Split className="w-4 h-4" />
                <span>Split URL Testing</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                완전히 다른 구조도<br />
                <span className="text-blue-600">URL 하나로 비교</span>하세요
              </h2>

              <p className="text-sm sm:text-lg text-gray-600 mb-8 leading-relaxed">
                페이지 전체를 리뉴얼했거나, 전혀 다른 레이아웃을 <br className="sm:hidden" />실험하고 싶으신가요?
                <br className="hidden sm:block" /> 트래픽을 여러 URL로 나누어 어떤 <br className="sm:hidden" />페이지가 더 성과가 좋은지 검증합니다.
              </p>

              <div className="space-y-6 mb-8">
                {[
                  { title: "자동 트래픽 분배", desc: "설정한 비율(50:50 등)대로 방문자를 자동 리다이렉트합니다.", icon: <RefreshCw className="w-5 h-5 text-blue-600" /> },
                  { title: "개발자 없이 즉시 시작", desc: "복잡한 서버 설정 없이, URL만 입력하면 바로 시작됩니다.", icon: <Clock className="w-5 h-5 text-blue-600" /> }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="mt-1 w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-white rounded-xl border border-blue-100 shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-2 text-sm">이럴 때 사용하세요:</h4>
                <ul className="space-y-2">
                  {[
                    "랜딩 페이지 전체 리뉴얼 전/후 비교",
                    "서로 다른 기술 스택으로 만든 페이지 비교",
                    "검색 광고(SEM) 랜딩 페이지 최적화"
                  ].map((text, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <CheckCircle2 className="w-4 h-4 text-green-500 mr-2" />
                      {text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 오른쪽: 인터랙티브 시뮬레이터 */}
            <div className="flex-1 w-full">
              <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden relative">
                {/* Header Mockup */}
                <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                  <span className="font-bold text-gray-700">트래픽 분배</span>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">진행 중</span>
                </div>

                <div className="p-8">
                  {/* Traffic Slider Visualization */}
                  <div className="mb-8">
                    <div className="flex justify-between text-sm font-semibold mb-4">
                      <span className="text-blue-600">기존안 ({trafficRatio}%)</span>
                      <span className="text-indigo-600">변경안 B ({100 - trafficRatio}%)</span>
                    </div>

                    <div className="relative h-4 bg-gray-100 rounded-full cursor-pointer group">
                      {/* 1. 왼쪽 파란색 막대 (transition 제거) */}
                      <div
                        className="absolute left-0 top-0 h-full bg-blue-500 rounded-l-full"
                        style={{ width: `${trafficRatio}%` }}
                      ></div>

                      {/* 2. 오른쪽 보라색 막대 (transition 제거) */}
                      <div
                        className="absolute right-0 top-0 h-full bg-indigo-500 rounded-r-full"
                        style={{ width: `${100 - trafficRatio}%` }}
                      ></div>

                      {/* Slider Thumb (Interactive) */}
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={trafficRatio}
                        onChange={(e) => setTrafficRatio(Number(e.target.value))}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-10"
                      />

                      {/* 3. 중앙 손잡이 아이콘 (transition 제거) */}
                      <div
                        className="absolute top-1/2 -translate-y-1/2 w-8 h-8 bg-white border-2 border-gray-200 rounded-full shadow-md flex items-center justify-center pointer-events-none z-0"
                        style={{ left: `calc(${trafficRatio}% - 16px)` }}
                      >
                        <Split className="w-4 h-4 text-gray-500" />
                      </div>
                    </div>
                    <p className="text-center text-xs text-gray-400 mt-2">드래그하여 트래픽 비율을 조정해보세요</p>
                  </div>

                  {/* Page Preview Cards */}
                  <div className="grid grid-cols-2 gap-6 relative">
                    {/* Redirect Lines (Animated) */}
                    <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-px h-6 bg-gray-200"></div>
                    <div className="absolute -top-6 left-1/4 right-1/4 h-px bg-gray-200"></div>
                    <div className="absolute -top-6 left-1/4 w-px h-6 bg-gray-200"></div>
                    <div className="absolute -top-6 right-1/4 w-px h-6 bg-gray-200"></div>

                    {/* Card A */}
                    <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100 text-center transition-all duration-300" style={{ opacity: trafficRatio / 100 + 0.3 }}>
                      <div className="text-xs font-mono text-gray-400 mb-2">/home-a</div>
                      <div className="w-full h-24 bg-white rounded shadow-sm mb-3 mx-auto border border-blue-100 relative overflow-hidden">
                        <div className="w-full h-2 bg-blue-200"></div>
                        <div className="w-1/2 h-2 bg-gray-100 mt-2 ml-2 rounded"></div>
                      </div>
                      <span className="text-sm font-bold text-blue-700 block">{trafficRatio}% 사용자</span>
                    </div>

                    {/* Card B */}
                    <div className="bg-indigo-50/50 rounded-xl p-4 border border-indigo-100 text-center transition-all duration-300" style={{ opacity: (100 - trafficRatio) / 100 + 0.3 }}>
                      <div className="text-xs font-mono text-gray-400 mb-2">/home-b</div>
                      <div className="w-full h-24 bg-white rounded shadow-sm mb-3 mx-auto border border-indigo-100 relative overflow-hidden">
                        <div className="w-full h-full bg-indigo-50 flex items-center justify-center">
                          <span className="text-[10px] text-indigo-300 font-bold">새로운 레이아웃</span>
                        </div>
                      </div>
                      <span className="text-sm font-bold text-indigo-700 block">{100 - trafficRatio}% 사용자</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-16">

            {/* 1. 설명 텍스트 (오른쪽 배치로 변화) */}
            <div className="flex-1 max-w-xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold mb-6">
                <Palette className="w-4 h-4" />
                <span>Visual Editor Testing</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                코딩은 잊으세요.<br />
                <span className="text-indigo-600">클릭하면 편집</span>됩니다.
              </h2>

              <p className="text-sm sm:text-lg text-gray-600 mb-8 leading-relaxed">
                개발자에게 요청하고 기다릴 필요가 없습니다.
                웹사이트 <br className="sm:hidden" />위에 에디터를 띄워 텍스트, 이미지, 색상을 실시간으로 <br className="sm:hidden" />수정하고 바로 테스트를 시작하세요.
              </p>

              <div className="space-y-6 mb-8">
                {[
                  { title: "텍스트 & 이미지 즉시 수정", desc: "오타 수정부터 메인 카피 변경까지 1초면 충분합니다.", icon: <Type className="w-5 h-5 text-indigo-600" /> },
                  { title: "요소 숨기기 & 이동", desc: "불필요한 요소를 숨기거나 위치를 드래그하여 조정하세요.", icon: <Layout className="w-5 h-5 text-indigo-600" /> }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 group">
                    <div className="mt-1 w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-100 transition-colors">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1 group-hover:text-indigo-600 transition-colors">{item.title}</h3>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-indigo-50/50 rounded-xl border border-indigo-100 shadow-sm">
                <h4 className="font-semibold text-gray-900 mb-2 text-sm">이럴 때 사용하세요:</h4>
                <ul className="space-y-2">
                  {[
                    "CTA 버튼 색상 및 문구(Copy) 최적화",
                    "메인 배너 이미지 A/B 테스트",
                    "가격 정책(Pricing) 표시 방식 변경"
                  ].map((text, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <CheckCircle2 className="w-4 h-4 text-indigo-500 mr-2" />
                      {text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 2. 인터랙티브 비주얼 에디터 (왼쪽) */}
            <div className="flex-1 w-full relative">
              {/* Background Blob */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-indigo-500/5 rounded-full blur-3xl -z-10"></div>

              <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden transform transition-transform hover:scale-[1.01] duration-500">

                {/* Browser Header */}
                <div className="bg-gray-100 border-b border-gray-200 px-4 py-3 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
                  </div>
                  <div className="flex-1 bg-white h-6 rounded border border-gray-200 mx-4 shadow-sm"></div>
                </div>

                {/* Website Content Mockup */}
                <div className="p-8 bg-gray-50 min-h-[400px] flex flex-col items-center justify-center relative">

                  {/* Editable Hero Section */}
                  <div className="w-full max-w-sm text-center space-y-4">
                    <div className="h-4 w-24 bg-gray-200 rounded mx-auto mb-2"></div>
                    <div className="h-8 w-3/4 bg-gray-300 rounded mx-auto"></div>
                    <div className="h-8 w-1/2 bg-gray-300 rounded mx-auto mb-6"></div>

                    {/* The Button (Target Element) */}
                    <div className="relative group/btn inline-block">
                      <button className="px-8 py-3 bg-blue-600 text-white rounded-lg font-medium shadow-md transition-all duration-300">
                        구매하기
                      </button>

                      {/* Selection Ring (Visual Editor Effect) */}
                      <div className="absolute -inset-2 border-2 border-indigo-500 border-dashed rounded-xl opacity-0 group-hover/btn:opacity-100 transition-opacity pointer-events-none">
                        <div className="absolute -top-3 -right-3 bg-indigo-500 text-white text-[10px] px-1.5 py-0.5 rounded font-bold shadow-sm">
                          Edit
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Floating Editor Panel (Glassmorphism) */}
                  <div className="absolute bottom-8 right-8 w-64 bg-white/90 backdrop-blur-xl border border-white/20 shadow-2xl rounded-xl p-4 animate-float ring-1 ring-black/5">
                    <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-100">
                      <span className="text-xs font-bold text-gray-500 uppercase">버튼 에디터</span>
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="text-[10px] text-gray-400 font-bold uppercase mb-1 block">텍스트</label>
                        <div className="bg-gray-50 border border-gray-200 rounded px-2 py-1.5 text-xs text-gray-700 font-medium">
                          구매하기|
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-400 font-bold uppercase mb-1 block">배경색</label>
                        <div className="flex gap-2">
                          <div className="w-5 h-5 rounded-full bg-blue-600 ring-1 ring-offset-1 ring-blue-300 cursor-pointer"></div>
                          <div className="w-5 h-5 rounded-full bg-indigo-600 cursor-pointer opacity-50 hover:opacity-100"></div>
                          <div className="w-5 h-5 rounded-full bg-emerald-500 cursor-pointer opacity-50 hover:opacity-100"></div>
                          <div className="w-5 h-5 rounded-full bg-rose-500 cursor-pointer opacity-50 hover:opacity-100"></div>
                        </div>
                      </div>
                      <button className="w-full mt-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold py-2 rounded shadow-sm transition-colors">
                        적용하기
                      </button>
                    </div>

                    {/* Cursor Animation */}
                    <MousePointer2 className="absolute bottom-20 right-24 w-6 h-6 text-black fill-black opacity-80 drop-shadow-xl animate-bounce-horizontal" />
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes bounce-horizontal {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(-5px); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-bounce-horizontal {
          animation: bounce-horizontal 2s ease-in-out infinite;
        }
      `}</style>
      </section>

      {/* 활용 사례 섹션 */}
      <section className="py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* 헤더 */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 border border-slate-700 text-slate-300 text-xs font-semibold uppercase tracking-wider mb-4">
              <TrendingUp className="w-3 h-3" />
              <span>Success Stories</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              작은 변화가 만들어낸 <br className="sm:hidden" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">놀라운 결과들</span>
            </h2>
            <p className="text-xl text-slate-400">
              실제 비즈니스 현장에서 검증된<br className="sm:hidden" /> A/B 테스트 성공 사례를 확인하세요.
            </p>
          </div>

          {/* 그리드 레이아웃 */}
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {cases.map((item, index) => (
              <div
                key={index}
                className="group relative bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-300 hover:-translate-y-1 overflow-hidden"
              >
                {/* 배경 그라데이션 (Hover 효과) */}
                <div className={`absolute top-0 right-0 w-64 h-64 bg-${item.color}-500/10 rounded-full blur-3xl -mr-16 -mt-16 transition-opacity opacity-0 group-hover:opacity-100`}></div>

                {/* 상단: 카테고리 & 아이콘 */}
                <div className="flex justify-between items-start mb-6 relative z-10">
                  <div className={`p-3 rounded-xl bg-white/5 border border-white/10 text-${item.color}-400 group-hover:text-white group-hover:bg-${item.color}-500 transition-colors`}>
                    {item.icon}
                  </div>
                  <span className="px-3 py-1 rounded-full bg-slate-800 text-xs font-medium text-slate-400 border border-slate-700">
                    {item.category}
                  </span>
                </div>

                {/* 타이틀 */}
                <h3 className="text-2xl font-bold text-white mb-6 relative z-10 group-hover:text-blue-200 transition-colors">
                  {item.title}
                </h3>

                {/* 문제 & 해결 (Timeline Style) */}
                <div className="space-y-4 mb-8 relative z-10">
                  <div className="flex gap-4">
                    <div className="mt-1 min-w-[24px]">
                      <XCircle className="w-5 h-5 text-red-400" />
                    </div>
                    <div>
                      <span className="text-xs font-bold text-red-400 uppercase tracking-wide block mb-1">발견된 문제</span>
                      <p className="text-sm text-slate-300 leading-relaxed">{item.problem}</p>
                    </div>
                  </div>

                  {/* 연결선 */}
                  <div className="w-0.5 h-4 bg-slate-700 ml-[11px]"></div>

                  <div className="flex gap-4">
                    <div className="mt-1 min-w-[24px]">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <span className="text-xs font-bold text-emerald-400 uppercase tracking-wide block mb-1">해결 방안</span>
                      <p className="text-sm text-slate-300 leading-relaxed">{item.solution}</p>
                    </div>
                  </div>
                </div>

                {/* 하단: 결과 위젯 */}
                <div className="relative z-10 bg-slate-800/50 rounded-xl p-4 border border-white/5 flex items-center justify-between group-hover:border-white/20 transition-colors">
                  <div>
                    {/* metricLabel (예: 클릭률 상승) */}
                    <div className="text-xs text-slate-400 mb-1 font-medium">{item.metricLabel}</div>
                    {/* metric (예: 3.5배) */}
                    <div className={`text-2xl font-bold text-${item.color}-400`}>{item.metric}</div>
                  </div>
                  <div className={`w-10 h-10 rounded-full bg-${item.color}-500/20 flex items-center justify-center text-${item.color}-400 group-hover:scale-110 transition-transform`}>
                    <ArrowUpRight className="w-6 h-6" />
                  </div>
                </div>

              </div>
            ))}
          </div>

        </div>
      </section>

      {/* 주요 특징 섹션 */}
      {/* 1. Comparison: 감 vs 데이터 (Table Style) */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              왜 데이터 기반이어야 할까요?
            </h2>
            <p className="text-gray-600">성공하는 조직은 '감' 대신 '실험'을 믿습니다</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
            {/* Header */}
            <div className="grid grid-cols-2 border-b border-gray-200 bg-gray-50/50">
              <div className="p-6 text-center border-r border-gray-200">
                <div className="text-red-500 font-bold text-lg mb-1">❌ 기존 방식 (Guesswork)</div>
                <div className="text-sm text-gray-500">직관과 경험에 의존</div>
              </div>
              <div className="p-6 text-center bg-blue-50/50">
                <div className="text-blue-600 font-bold text-lg mb-1">⭕ A/B 테스트 (Data-Driven)</div>
                <div className="text-sm text-blue-600/70">데이터, 통계에 의존</div>
              </div>
            </div>

            {/* Rows */}
            <div className="divide-y divide-gray-100">
              {[
                { left: "의견 충돌로 인한 의사결정 지연", right: "데이터 기반의 즉각적인 합의" },
                { left: "실패 시 원인을 알 수 없음", right: "성공/실패의 정확한 원인 파악" },
                { left: "전면 리뉴얼로 인한 큰 리스크", right: "점진적 개선으로 리스크 최소화" },
                { left: "고객이 좋아할 것이라 '추측'", right: "고객이 선택한 것을 '확인'" }
              ].map((row, i) => (
                <div key={i} className="grid grid-cols-2 hover:bg-gray-50 transition-colors">
                  <div className="p-5 text-gray-600 border-r border-gray-100 text-sm md:text-base flex items-center justify-center text-center break-keep">{row.left}</div>
                  <div className="p-5 text-gray-900 font-medium bg-blue-50/10 text-sm md:text-base flex items-center justify-center text-center break-keep">{row.right}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 2. Synergy Section: 퍼널 + 히트맵 + A/B 테스트 통합 */}
      <section className="py-24 bg-white relative overflow-hidden">
        {/* 배경 장식 */}
        <div className="absolute top-1/2 left-0 w-96 h-96 bg-purple-100/50 rounded-full blur-[100px] -translate-y-1/2 -translate-x-1/3 pointer-events-none"></div>
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-blue-100/50 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

          {/* 1. 메인 시너지 영역 (좌우 분할) */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20">

            {/* Left: 설득 텍스트 */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-50 border border-purple-100 text-purple-600 text-sm font-bold mb-6">
                <Zap className="w-4 h-4 fill-purple-600" />
                <span>Full Loop</span>
              </div>

              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                <span className="text-purple-500">퍼널</span>로 어디를 찾고,<br />
                <span className="text-orange-500">히트맵</span>으로 경향을 보고,<br />
                <span className="text-cyan-600">세션 녹화</span>로 장면을 확인하고,<br />
                <span className="text-blue-600">A/B 테스트</span>로 해결하세요
              </h2>

              <p className="text-sm sm:text-lg text-gray-600 mb-8 leading-relaxed">
                히트맵만으로는 문제를 '발견'할 뿐, '해결'할 수는 없습니다.
                SwitchUp의 통합 플랫폼에서 발견 → 경향 파악 → 장면 확인 → 검증까지
                끊김 없이 진행하세요.
              </p>

              <div className="space-y-4">
                {/* Step 1 */}
                <div className="flex items-center gap-4 bg-purple-50/50 p-4 rounded-xl border border-purple-100">
                  <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold">1</div>
                  <div>
                    <div className="font-bold text-gray-900">병목 발견 (Funnel)</div>
                    <div className="text-sm text-gray-500">"장바구니→결제에서 62% 이탈" → 위치 파악</div>
                  </div>
                </div>

                <div className="pl-7">
                  <ArrowDown className="w-5 h-5 text-gray-300" />
                </div>

                {/* Step 2 */}
                <div className="flex items-center gap-4 bg-orange-50/50 p-4 rounded-xl border border-orange-100">
                  <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-bold">2</div>
                  <div>
                    <div className="font-bold text-gray-900">경향 파악 (Heatmap)</div>
                    <div className="text-sm text-gray-500">"결제 페이지에서 배송비 영역 반복 클릭" → 의심 지점</div>
                  </div>
                </div>

                <div className="pl-7">
                  <ArrowDown className="w-5 h-5 text-gray-300" />
                </div>

                {/* Step 3 - Session Replay */}
                <div className="flex items-center gap-4 bg-cyan-50/50 p-4 rounded-xl border border-cyan-100">
                    <div className="w-10 h-10 rounded-full bg-cyan-100 text-cyan-600 flex items-center justify-center font-bold">3</div>
                    <div>
                        <div className="font-bold text-gray-900">장면 확인 (Session Replay)</div>
                        <div className="text-sm text-gray-500">"배송비 안내가 접혀 있어 못 찾고 이탈" → 원인 확정</div>
                    </div>
                </div>

                <div className="pl-7">
                    <ArrowDown className="w-5 h-5 text-gray-300" />
                </div>

                {/* Step 3 */}
                <div className="flex items-center gap-4 bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                  <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold">4</div>
                  <div>
                    <div className="font-bold text-gray-900">가설 검증 (A/B Test)</div>
                    <div className="text-sm text-gray-500">"무료배송 조건 표시 vs 기존" → 성능 비교</div>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/service/funnel">
                  <button className="flex items-center gap-2 text-purple-600 font-bold hover:text-purple-700 transition-colors group">
                    퍼널 분석 보기
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </Link>
                <Link to="/service/heatmap">
                  <button className="flex items-center gap-2 text-orange-600 font-bold hover:text-orange-700 transition-colors group">
                    히트맵 분석 보기
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </Link>
                  <Link to="/service/replay">
                      <button className="flex items-center gap-2 text-cyan-600 font-bold hover:text-cyan-700 transition-colors group">
                          세션 녹화 보기
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </button>
                  </Link>
              </div>
            </div>

            {/* Right: 시각화 카드 */}
            <div className="relative w-full max-w-md mx-auto lg:max-w-none">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-100 to-blue-100 rounded-3xl transform rotate-3 scale-105 opacity-60 blur-xl"></div>

              <div className="relative bg-white border border-gray-200 rounded-3xl p-8 shadow-xl">

                {/* Flow Visualization - 세로 */}
                <div className="flex flex-col items-center gap-5 mb-8">

                  {/* Step 1: Funnel */}
                  <div className="text-center group">
                    <div className="w-20 h-20 bg-purple-50 border border-purple-100 rounded-2xl flex items-center justify-center text-purple-500 mb-3 mx-auto shadow-sm group-hover:scale-105 transition-transform">
                      <Filter className="w-10 h-10 fill-purple-500" />
                    </div>
                    <div className="font-bold text-gray-900">병목 발견</div>
                    <div className="text-xs text-gray-500 font-mono mt-1">퍼널 분석</div>
                  </div>

                  <ArrowDown className="w-6 h-6 text-gray-300" />

                  {/* Step 2: Heatmap */}
                  <div className="text-center group">
                    <div className="w-20 h-20 bg-orange-50 border border-orange-100 rounded-2xl flex items-center justify-center text-orange-500 mb-3 mx-auto shadow-sm group-hover:scale-105 transition-transform">
                      <Flame className="w-10 h-10 fill-orange-500" />
                    </div>
                    <div className="font-bold text-gray-900">경향 파악</div>
                    <div className="text-xs text-gray-500 font-mono mt-1">히트맵 분석</div>
                  </div>

                  <ArrowDown className="w-6 h-6 text-gray-300" />

                  {/* 3. Session Replay */}
                  <div className="text-center group">
                      <div className="w-20 h-20 bg-cyan-50 border border-cyan-100 rounded-2xl flex items-center justify-center text-cyan-500 mb-3 mx-auto shadow-sm group-hover:scale-105 transition-transform">
                          <Video className="w-10 h-10 text-cyan-500 fill-cyan-500" />
                      </div>
                      <div className="font-bold text-gray-900">장면 확인</div>
                      <div className="text-xs text-gray-500 font-mono mt-1">세션 녹화</div>
                  </div>

                  <ArrowDown className="w-6 h-6 text-gray-300" />

                  {/* Step 3: A/B Test */}
                  <div className="text-center group">
                    <div className="w-20 h-20 bg-blue-50 border border-blue-100 rounded-2xl flex items-center justify-center text-blue-600 mb-3 mx-auto shadow-sm group-hover:scale-105 transition-transform">
                      <Split className="w-10 h-10" />
                    </div>
                    <div className="font-bold text-gray-900">가설 검증</div>
                    <div className="text-xs text-gray-500 font-mono mt-1">A/B 테스트</div>
                  </div>
                </div>

                {/* Result Box */}
                <div className="bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-2xl p-6 text-center shadow-inner relative overflow-hidden group hover:border-green-200 transition-colors">
                  <div className="absolute top-0 left-0 w-1 h-full bg-green-500"></div>
                  <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Total Impact</div>
                  <div className="flex items-center justify-center gap-4">
                    <span className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-500">
                      +158%
                    </span>
                    <div className="text-left leading-tight">
                      <div className="text-sm font-bold text-gray-900">전환율 상승</div>
                      <div className="text-[10px] text-gray-500">평균 개선 효과</div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* 2. 하단 연동 기능 카드 */}
          <div className="grid md:grid-cols-2 gap-6">

            <div className="bg-gradient-to-br from-purple-50 to-violet-50/30 p-8 rounded-2xl border border-purple-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 text-purple-600 shadow-sm group-hover:scale-110 transition-transform">
                <Layers className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">퍼널 + 히트맵 연동 분석</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                퍼널에서 이탈률이 높은 페이지를 발견하면, 해당 페이지의 히트맵을 즉시 확인하세요.
                클릭맵과 스크롤맵으로 이탈 원인을 시각적으로 파악할 수 있습니다.
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50/30 p-8 rounded-2xl border border-blue-100 hover:shadow-lg transition-all group">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mb-4 text-blue-600 shadow-sm group-hover:scale-110 transition-transform">
                <Layers className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">A/B 테스트 버전별 히트맵 비교</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                A안과 B안의 히트맵을 나란히 놓고 비교해보세요.
                디자인 변경이 사용자의 클릭과 스크롤에 어떤 영향을 줬는지 시각적으로 즉시 확인할 수 있습니다.
              </p>
            </div>

          </div>

        </div>
      </section>

      <section className="relative py-32 overflow-hidden bg-slate-900">
        {/* 1. 배경 텍스처 & 그라데이션 */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/40 via-slate-900 to-slate-900"></div>

        {/* 바닥에서 올라오는 빛 효과 */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] bg-blue-600/20 rounded-full blur-[120px] pointer-events-none"></div>

        {/* 별/반짝임 장식 (CSS 애니메이션) */}
        <div className="absolute top-20 left-20 w-1 h-1 bg-white rounded-full animate-pulse"></div>
        <div className="absolute top-40 right-40 w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse delay-700"></div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">

          {/* 상단 배지 */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300 text-sm font-medium mb-8 animate-fade-in-up">
            <Sparkles className="w-4 h-4" />
            <span>Start testing today</span>
          </div>

          {/* 메인 카피 */}
          {/* text-4xl -> text-3xl로 변경 */}
          <h2 className="text-3xl md:text-6xl font-bold text-white mb-8 tracking-tight leading-tight">
            실험하지 않는 비즈니스는 <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-300">성장할 수 없습니다.</span>
          </h2>

          {/* 서브 카피 */}
          <p className="text-lg md:text-xl text-slate-400 mb-12 max-w-2xl mx-auto leading-relaxed">
            더 이상 회의실에서 추측하지 마세요. 고객에게 직접 물어보는 가장 쉬운 방법.<br className="hidden md:block" />
            지금 바로 <br className="sm:hidden" />데이터 기반 성장을 시작하세요.
          </p>

          {/* 버튼 그룹 */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/freestart" className="w-full sm:w-auto">
              <button className="w-full px-8 py-4 bg-white text-slate-900 rounded-xl font-bold text-lg hover:bg-blue-50 transition-all shadow-[0_0_40px_-10px_rgba(255,255,255,0.3)] hover:shadow-[0_0_60px_-15px_rgba(255,255,255,0.5)] hover:-translate-y-1 flex items-center justify-center gap-2 group">
                무료로 시작하기
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>
            <Link to="/contact" className="w-full sm:w-auto">
              <button className="w-full px-8 py-4 bg-transparent border border-slate-700 text-slate-300 rounded-xl font-medium hover:bg-slate-800 hover:text-white transition-all">
                도입 문의하기
              </button>
            </Link>
          </div>

          {/* 하단 신뢰 요소 (Trust Badges) */}
          <div className="mt-12 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-slate-500 font-medium">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-blue-500" /> 30일 무료 체험
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-blue-500" /> 신용카드 불필요
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-blue-500" /> 언제든 취소 가능
            </div>
          </div>

        </div>
      </section>

      {/* <SimpleKakaoButton
        channelId="_yixgxjn"
        buttonText="카카오로 문의하기"
        mobileText="카카오 문의"
      /> */}

      <Footer />
    </div>
  );
};

export default ServiceAbtest;